import { useEffect, useState } from 'react';
import { ClientListItem } from 'src/admin/users/types';
import {
  SelectPeopleModal,
  type SelectPeopleSection,
  type SelectPersonRow,
} from 'src/design-system/SelectPeopleModal';
import { financialItemNoun } from 'src/financial/financial-data/financial-item/financialItemNoun';
import { ownerCandidates } from 'src/financial/financial-data/financial-item/ownerCandidates';
import { FinancialCategory } from 'src/financial/financial-data/hooks/useFinancialItems';
import { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

export type OwnerSelection = { client_id: string } | { external_name: string };

type FamilyOwnerModalProps = {
  opened: boolean;
  onClose: () => void;
  members: FamilyMember[];
  /** The clients the viewer can add as owners besides the family members. */
  otherClients?: ClientListItem[];
  currentClientId: string;
  /** Names the thing being added in the title. Absent only while the picker is closed. */
  category?: FinancialCategory;
  /** Members ticked when the picker opens. Defaults to the client the flow is anchored on. */
  initialClientIds?: string[];
  /** A member who stays ticked: the client whose page the item is added from must own it. */
  lockedClientId?: string;
  onContinue: (owners: OwnerSelection[]) => void;
};

type ExternalOwner = { id: string; name: string };

const fullName = (client: ClientListItem) =>
  formatName(client.firstName, client.lastName, NameFormat.NATURAL);

export const FamilyOwnerModal = ({
  opened,
  onClose,
  members,
  otherClients = [],
  currentClientId,
  category,
  initialClientIds,
  lockedClientId,
  onContinue,
}: FamilyOwnerModalProps) => {
  // Opening resets the picker to its preselection. Callers rebuild that array on every render,
  // so the reset keys off its contents rather than its identity.
  const preselectedKey = (initialClientIds ?? [currentClientId]).join(',');
  const preselectedIds = () => (preselectedKey === '' ? [] : preselectedKey.split(','));
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>(preselectedIds);
  const [outsideClients, setOutsideClients] = useState<ClientListItem[]>([]);
  const [externals, setExternals] = useState<ExternalOwner[]>([]);
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    if (!opened) return;
    setSelectedClientIds(preselectedKey === '' ? [] : preselectedKey.split(','));
    setOutsideClients([]);
    setExternals([]);
    setSeq(0);
  }, [opened, preselectedKey]);

  const toggleClient = (clientId: string) =>
    setSelectedClientIds(previous =>
      previous.includes(clientId)
        ? previous.filter(id => id !== clientId)
        : [...previous, clientId],
    );

  const memberRows: SelectPersonRow[] = members.map(member => {
    const locked = member.clientId === lockedClientId;
    return {
      id: member.clientId,
      name: fullName(member.client),
      role: member.roleLabel ?? 'Family member',
      checked: locked || selectedClientIds.includes(member.clientId),
      disabled: locked,
      onToggle: () => {
        if (!locked) toggleClient(member.clientId);
      },
    };
  });

  const outsideClientRows: SelectPersonRow[] = outsideClients.map(client => ({
    id: client.id,
    name: fullName(client),
    role: 'Client',
    checked: true,
    onToggle: () =>
      setOutsideClients(previous => previous.filter(picked => picked.id !== client.id)),
  }));

  const externalRows: SelectPersonRow[] = externals.map(external => ({
    id: external.id,
    name: external.name,
    role: 'External',
    checked: true,
    onToggle: () => setExternals(previous => previous.filter(item => item.id !== external.id)),
  }));

  const itemNoun = financialItemNoun(category);
  const offeredClientIds = new Set([
    ...members.map(member => member.clientId),
    ...outsideClients.map(client => client.id),
  ]);

  const section: SelectPeopleSection = {
    key: 'owners',
    title: 'Owners',
    requiredMessage:
      externals.length > 0
        ? `Choose at least one family member. External owners cannot own this ${itemNoun} on their own.`
        : 'Choose at least one family member.',
    addLabel: 'Add owner',
    searchPlaceholder: 'Search clients or type a name',
    addAsWord: 'external owner',
    allowFreeText: true,
    rows: [...memberRows, ...outsideClientRows, ...externalRows],
    getResults: search =>
      search.trim() === ''
        ? []
        : ownerCandidates(otherClients, offeredClientIds, search).map(client => ({
            id: client.id,
            name: fullName(client),
            email: client.emailAddress ?? undefined,
            onAdd: () => setOutsideClients(previous => [...previous, client]),
          })),
    onAddFreeText: typed => {
      setExternals(previous => [...previous, { id: `external:${seq}`, name: typed }]);
      setSeq(previous => previous + 1);
    },
  };

  const memberClientIds =
    lockedClientId && !selectedClientIds.includes(lockedClientId)
      ? [lockedClientId, ...selectedClientIds]
      : selectedClientIds;
  // An item is opened through a family member, so one of them must own it.
  const hasFamilyOwner = memberClientIds.length > 0;

  const handleConfirm = () => {
    const owners: OwnerSelection[] = [
      ...memberClientIds.map(id => ({ client_id: id })),
      ...outsideClients.map(client => ({ client_id: client.id })),
      ...externals.map(external => ({ external_name: external.name })),
    ];
    onContinue(owners);
  };

  return (
    <SelectPeopleModal
      opened={opened}
      onClose={onClose}
      title={`Select owners of this ${itemNoun}`}
      subtitle="You can also add other clients or external owners here"
      confirmLabel="Continue"
      confirmDisabled={!hasFamilyOwner}
      sections={[section]}
      validationTriggered={!hasFamilyOwner}
      onConfirm={handleConfirm}
    />
  );
};
